import React, { useEffect, useMemo, useState } from 'react';

import ErrorPage from '@/components/Error';
import LoadingSpinner from '@/components/LoadingSpinner';
import Redirect from '@/components/Redirect';
import { selectAuthLoading } from '@/features/auth/selectors';
import {
  getShouldEnableMfa,
  selectHasFeatureFlagsLoaded,
  selectIsAnyFeatureFlagLoading
} from '@/features/featureFlags/selectors';
import { fetchFeatureFlags } from '@/features/featureFlags/slice';
import LoginForm from '@/features/login/components/LoginFormContainer';
import LoginSuccess from '@/features/login/components/LoginSuccess';
import { selectIsSignInLoading, selectShouldShowLoginForm } from '@/features/login/selectors';
import {
  fetchDevices,
  isSignInWithSessionTokenError,
  signInWithSessionToken
} from '@/features/login/slice';
import { MfaContainer } from '@/features/mfa/components';
import {
  selectFlowId,
  selectIsMfaSignInLoading,
  selectIsMfaSkipped,
  selectMfaStatus,
  selectOtpRequested,
  selectShouldShowMfaContainer,
  selectShouldShowMfaDevicesContainer
} from '@/features/mfa/selectors';
import { fetchFlowStatus } from '@/features/mfa/slice';
import { selectHasFetchedDevices } from '@/features/mfaDevices/selectors';
import useRedirectUrl from '@/features/redirectUrl/hooks/useRedirectUrl';
import { selectShouldRedirect } from '@/features/redirectUrl/selectors';
import {
  selectHasSessionToken,
  selectHasVenueIds,
  selectUserEmail
} from '@/features/user/selectors';
import { useEffectOnceWhen } from '@/hooks/useEffectOnceWhen';
import { cookieService } from '@/services/cookieService';

import * as Sentry from '@sentry/react';
import { isNil } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

const AuthCheck = () => {
  const dispatch = useDispatch();

  // Conditional selectors
  const shouldShowMfaDevicesContainer = useSelector(selectShouldShowMfaDevicesContainer);
  const shouldShowLoginForm = useSelector(selectShouldShowLoginForm);
  const shouldRedirectAfterLogin = useSelector(selectShouldRedirect);
  const shouldShowMfaContainer = useSelector(selectShouldShowMfaContainer);

  // Feature flag selectors
  const shouldEnableMfa = useSelector(getShouldEnableMfa);

  // Error selectors
  const isSessionTokenError = useSelector(isSignInWithSessionTokenError);

  // Prop selectors
  const flowId = useSelector(selectFlowId);
  const userEmail = useSelector(selectUserEmail);
  const hasSessionCookie = cookieService.getCookie('session_token') !== null;
  const hasFetchedSession = useSelector((state) => state.auth.hasFetchedSession);
  const hasSessionToken = useSelector(selectHasSessionToken);
  const hasUserId = useSelector((state) => state.user.id !== null);
  const hasEmail = useSelector((state) => state.user.email !== null);
  const hasVenueIds = useSelector(selectHasVenueIds);
  const isMfaSkipped = useSelector(selectIsMfaSkipped);
  const otpRequested = useSelector(selectOtpRequested);
  const mfaStatus = useSelector(selectMfaStatus);

  // Loading selectors
  const isSignInLoading = useSelector(selectIsSignInLoading);
  const hasFetchedFeatureFlags = useSelector(selectHasFeatureFlagsLoaded);
  const isAuthLoading = useSelector(selectAuthLoading);
  const isAnyFeatureFlagLoading = useSelector(selectIsAnyFeatureFlagLoading);
  const isMfaSignInLoading = useSelector(selectIsMfaSignInLoading);
  const isFetchDevicesLoading = useSelector((state) => state.mfa.isFetchDevicesLoading);
  const hasFetchedDevices = useSelector(selectHasFetchedDevices);

  // URL Param selectors
  const redirectUrl = useRedirectUrl();

  // Local state
  const [redirectUrlState, setRedirectUrlState] = useState();

  // Side effects
  useEffect(() => {
    if (redirectUrl) {
      setRedirectUrlState(redirectUrl);
    }
  }, [redirectUrl]);

  useEffectOnceWhen(() => {
    dispatch(fetchDevices());
  }, [hasSessionToken, !isFetchDevicesLoading, !hasFetchedDevices, hasUserId, hasEmail]);

  useEffectOnceWhen(() => {
    dispatch(fetchFeatureFlags());
  }, [hasSessionToken, hasVenueIds]);

  useEffectOnceWhen(() => {
    dispatch(signInWithSessionToken());
  }, [hasSessionToken, !hasFetchedSession]);

  useEffect(() => {
    if (mfaStatus === 'INIT' && otpRequested && flowId && userEmail) {
      dispatch(fetchFlowStatus({ flowId, email: userEmail }));
    }
  }, [mfaStatus, otpRequested, flowId, userEmail, dispatch]);

  const shouldShowSpinner = useMemo(() => {
    return (
      isSignInLoading ||
      // isAuthLoading ||
      !hasFetchedSession ||
      isAnyFeatureFlagLoading ||
      isMfaSignInLoading
    );
  }, [
    isSignInLoading,
    isAuthLoading,
    hasFetchedSession,
    isAnyFeatureFlagLoading,
    isMfaSignInLoading
  ]);

  // Renderers
  if (isAnyFeatureFlagLoading) {
    return <LoadingSpinner />;
  }
  if (isSessionTokenError) {
    return <ErrorPage error="Invalid token. Please log in again." />;
  }
  if (shouldShowLoginForm) {
    return <LoginForm />;
  }
  if (
    hasFetchedFeatureFlags &&
    hasSessionCookie &&
    !shouldEnableMfa &&
    !isNil(redirectUrlState && !shouldShowSpinner)
  ) {
    return <Redirect redirectUrl={redirectUrlState} />;
  }
  if (shouldShowMfaContainer) {
    return <MfaContainer />;
  }
  if (shouldShowMfaDevicesContainer && !redirectUrl) {
    return <LoginSuccess />;
  }
  if (shouldRedirectAfterLogin && !isNil(redirectUrl) && !shouldShowSpinner) {
    return <Redirect redirectUrl={redirectUrlState} />;
  }
  if (shouldShowSpinner) {
    return <LoadingSpinner />;
  }
  if (
    hasSessionCookie &&
    hasFetchedSession &&
    !isSignInLoading &&
    !redirectUrlState &&
    (!shouldEnableMfa || isMfaSkipped)
  ) {
    return <LoginSuccess />;
  }

  // Fallback renderer
  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage isFallback />}>
      <LoginSuccess />
    </Sentry.ErrorBoundary>
  );
};

export default React.memo(AuthCheck);
